'use client';

import type { AiCreateSkillRejectedResponse } from '@terra/shared-contracts-ai-skills';
import { type SkillMutationResponse } from '@terra/shared-contracts-ai-skills';
import {
  Button,
  Scroll,
  Spinner,
  Text,
  toasterApi,
  type ChatMentionsMenuItemType,
} from '@terra/ui-design-system';
import {
  SkillEditorForm,
  getSkillEditorValidation,
  useSkill,
  useSkillsMutations,
  SkillsApiError,
  type SkillEditorTouchedFields,
  type SkillEditorField,
} from '@terra/ui-skills';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { ArrowLeft02Icon, CancelCircleIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Tab } from '../../store/layoutStore';
import { useMainContentTabs } from '../../store/layoutStore';
import {
  EMPTY_SKILL_DRAFT,
  useSkillDraftsStore,
} from '../../store/skillDraftsStore';
import { useCreateNewChatTab } from '../chat/actions/useCreateNewChatTab';
import { useChatTabs } from '../../store/chatTabsStore';
import { useAppTargetId } from '../../store/projectStore';
import {
  createSkillFieldContextMention,
  createSkillMention,
} from '../../utils/skillMentionUtils';
import { getDraftFromSkill } from '../../utils/skillDraftUtils';
import {
  useSkillsEditorLabels,
  useSkillsListLabels,
} from '../../hooks/useSkillLabels';
import { readObjectValue } from '../../utils/readObjectValue';

type SkillEditorTab = Extract<
  Tab,
  {
    type: 'skill-editor';
  }
>;
const TOUCHED_ALL: SkillEditorTouchedFields = {
  slug: true,
  name: true,
  description: true,
  body: true,
};
const TOUCHED_NONE: SkillEditorTouchedFields = {
  slug: false,
  name: false,
  description: false,
  body: false,
};

function isAiCreateRejection(
  value: unknown,
): value is AiCreateSkillRejectedResponse {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const reason = readObjectValue(value, 'reason');
  const message = readObjectValue(value, 'message');

  return typeof reason === 'string' && typeof message === 'string';
}

export function SkillEditorContent({ tab }: { tab: SkillEditorTab }) {
  const { skillDraftId, mode, skillId } = tab.data;
  const tCommon = useTranslations('common');
  const tProxyMain = useTranslations('proxy.mainContentArea');
  const {
    tabs,
    updateTab,
    openTab,
    closeTab,
    setActiveTab,
    navigateInActiveTab,
  } = useMainContentTabs();
  const { chatTabs, setActiveChatTabId, updateChatTab } = useChatTabs();
  const { createNewChatTab } = useCreateNewChatTab();
  const appTargetId = useAppTargetId();
  const draft =
    useSkillDraftsStore((state) => state.drafts[skillDraftId]) ??
    EMPTY_SKILL_DRAFT;
  const draftUpdateEffect = useSkillDraftsStore(
    (state) => state.updateEffects[skillDraftId],
  );
  const setDraft = useSkillDraftsStore((state) => state.setDraft);
  const [touched, setTouched] =
    useState<SkillEditorTouchedFields>(TOUCHED_NONE);
  const [creatorError, setCreatorError] =
    useState<AiCreateSkillRejectedResponse | null>(null);
  const [pendingAction, setPendingAction] = useState<'save' | 'publish' | null>(
    null,
  );
  const { skill, isLoading } = useSkill({
    skillId: skillId ?? '',
    applicationId: appTargetId,
    enabled: mode === 'edit' && Boolean(skillId),
  });
  const { aiCreateSkill, updateSkillDraft, publishSkill } = useSkillsMutations({
    applicationId: appTargetId,
  });
  const labels = useSkillsEditorLabels();
  const listLabels = useSkillsListLabels();
  const validation = useMemo(
    () =>
      getSkillEditorValidation({
        draft,
        touched,
        mode,
        labels,
      }),
    [draft, labels, mode, touched],
  );

  useEffect(() => {
    if (
      mode !== 'edit' ||
      !skill ||
      useSkillDraftsStore.getState().drafts[skillDraftId]
    ) {
      return;
    }

    setDraft(skillDraftId, getDraftFromSkill(skill));
  }, [mode, setDraft, skill, skillDraftId]);
  const derivedTabTitle = useMemo(
    () =>
      draft.name.trim().length > 0
        ? draft.name
        : mode === 'create'
          ? labels.createTitle
          : labels.editTitle,
    [draft.name, labels.createTitle, labels.editTitle, mode],
  );

  useEffect(() => {
    updateTab(tab.id, { title: derivedTabTitle });
  }, [derivedTabTitle, tab.id, updateTab]);
  const handleReturnToSkillsPage = useCallback(() => {
    navigateInActiveTab({
      id: 'skills-list',
      title: listLabels.title,
      type: 'skills-list',
    });
  }, [listLabels.title, navigateInActiveTab]);
  const handleOpenSavedSkill = useCallback(
    (savedSkill: SkillMutationResponse) => {
      const savedSkillTitle =
        savedSkill.draftVersion?.frontmatter.name ??
        savedSkill.publishedVersion?.frontmatter.name ??
        savedSkill.slug;
      const existingSkillTab = tabs.find(
        (existingTab) =>
          existingTab.type === 'skill' &&
          existingTab.data.skillId === savedSkill.skillId,
      );

      if (existingSkillTab) {
        setActiveTab(existingSkillTab.id);
      } else {
        openTab({
          id: `skill-${savedSkill.skillId}`,
          title: savedSkillTitle,
          type: 'skill',
          data: { skillId: savedSkill.skillId },
        });
      }

      closeTab(tab.id);
    },
    [closeTab, openTab, setActiveTab, tab.id, tabs],
  );
  const createCurrentSkillMention = useCallback(
    () =>
      createSkillMention({
        skillDraftId,
        skillId,
        mode,
        label: draft.name || draft.slug,
        fallbackLabel:
          mode === 'create' ? labels.createTitle : labels.editTitle,
      }),
    [
      draft.name,
      draft.slug,
      labels.createTitle,
      labels.editTitle,
      mode,
      skillDraftId,
      skillId,
    ],
  );
  const seedSkillBuilderChat = useCallback(
    (draftInput: string, contextMentions: ChatMentionsMenuItemType[] = []) => {
      const existingChat = chatTabs.find(
        (chatTab) => chatTab.skillDraftId === skillDraftId,
      );
      const skillMention = createCurrentSkillMention();

      if (existingChat) {
        const existingContextMentions =
          existingChat.mentions?.filter(
            (mention) => mention.id !== skillMention.id,
          ) ?? [];
        const mentions = [
          skillMention,
          ...existingContextMentions,
          ...contextMentions,
        ];

        updateChatTab(existingChat.tabId, {
          draftInput: existingChat.draftInput?.trim()
            ? existingChat.draftInput
            : draftInput,
          mentions,
        });
        setActiveChatTabId(existingChat.tabId);

        return;
      }

      const mentions = [skillMention, ...contextMentions];

      createNewChatTab({
        skillDraftId,
        initialDraftInput: draftInput,
        initialMentions: mentions,
      });
    },
    [
      chatTabs,
      createCurrentSkillMention,
      createNewChatTab,
      setActiveChatTabId,
      skillDraftId,
      updateChatTab,
    ],
  );
  const linkedChatTab = useMemo(
    () => chatTabs.find((chatTab) => chatTab.skillDraftId === skillDraftId),
    [chatTabs, skillDraftId],
  );

  useEffect(() => {
    if (!linkedChatTab) {
      return;
    }

    const skillMention = createCurrentSkillMention();
    const existingSkillMention = linkedChatTab.mentions?.find(
      (mention) => mention.id === skillMention.id,
    );
    const isSkillMentionCurrent =
      existingSkillMention?.label === skillMention.label &&
      existingSkillMention.mentionEntity?.label ===
        skillMention.mentionEntity?.label;

    if (isSkillMentionCurrent) {
      return;
    }

    const mentionsWithoutCurrentSkill =
      linkedChatTab.mentions?.filter(
        (mention) => mention.id !== skillMention.id,
      ) ?? [];

    updateChatTab(linkedChatTab.tabId, {
      mentions: [skillMention, ...mentionsWithoutCurrentSkill],
    });
  }, [createCurrentSkillMention, linkedChatTab, updateChatTab]);
  const handleRefineField = useCallback(
    ({
      field,
      label,
      value,
      isSelection,
    }: {
      field: SkillEditorField;
      label: string;
      value: string;
      isSelection: boolean;
    }) => {
      const normalizedValue = value.trim() || labels.emptyField;
      const requestId = Date.now();
      const contextMention = createSkillFieldContextMention({
        skillDraftId,
        skillId,
        mode,
        field,
        label,
        value: normalizedValue,
        contextLabel: isSelection
          ? labels.fieldContextSelectionLabel
          : labels.fieldContextFieldLabel,
        contextDisplayLabel: isSelection
          ? labels.fieldContextSelectedText
          : labels.fieldContextEntireField,
        requestedAction: labels.refineFieldChatRequest,
        rawContextTitle: labels.fieldContextRawTitle,
        rawContextUntrustedDataNotice:
          labels.fieldContextRawUntrustedDataNotice,
        requestId,
      });

      seedSkillBuilderChat(labels.refineFieldChatRequest, [contextMention]);
    },
    [
      labels.emptyField,
      labels.fieldContextEntireField,
      labels.fieldContextFieldLabel,
      labels.fieldContextSelectedText,
      labels.fieldContextSelectionLabel,
      labels.fieldContextRawTitle,
      labels.fieldContextRawUntrustedDataNotice,
      labels.refineFieldChatRequest,
      mode,
      seedSkillBuilderChat,
      skillDraftId,
      skillId,
    ],
  );
  const seedRejectionInChat = useCallback(
    (rejection: AiCreateSkillRejectedResponse) => {
      const details = rejection.details?.filter(Boolean) ?? [];
      const issueLines =
        rejection.issues?.flatMap((issue) => [
          labels.safetyReviewChatIssue({
            category: issue.category,
            severity: issue.severity,
            field: issue.field,
            message: issue.message,
          }),
          ...(issue.excerpt
            ? [labels.safetyReviewChatExcerpt(issue.excerpt)]
            : []),
        ]) ?? [];

      seedSkillBuilderChat(
        [
          labels.safetyReviewChatTitle,
          rejection.message,
          '',
          ...(issueLines.length > 0
            ? issueLines
            : details.map(labels.safetyReviewChatDetail)),
          '',
          labels.safetyReviewChatRequest,
        ].join('\n'),
      );
    },
    [labels, seedSkillBuilderChat],
  );
  const handleSkillRejection = useCallback(
    (rejection: AiCreateSkillRejectedResponse) => {
      setCreatorError(rejection);
      seedRejectionInChat(rejection);
      const details = rejection.details?.filter(Boolean) ?? [];

      toasterApi.error({
        title: labels.aiCreateRejectedTitle,
        description:
          details.length > 0
            ? `${rejection.message} ${details.join(' ')}`
            : rejection.message,
      });
    },
    [labels.aiCreateRejectedTitle, seedRejectionInChat],
  );
  const handleSave = useCallback(
    async ({ publishImmediately }: { publishImmediately: boolean }) => {
      setCreatorError(null);
      setTouched(TOUCHED_ALL);
      setPendingAction(publishImmediately ? 'publish' : 'save');
      const touchedValidation = getSkillEditorValidation({
        draft,
        touched: TOUCHED_ALL,
        mode,
        labels,
      });

      if (touchedValidation.hasBlockingErrors) {
        setPendingAction(null);

        return;
      }

      if (mode === 'edit' && !skillId) {
        toasterApi.error({
          title: labels.errorTitle,
          description: labels.error,
        });
        setPendingAction(null);

        return;
      }

      let savedSkill: SkillMutationResponse | null = null;

      try {
        if (mode === 'create') {
          if (!appTargetId) {
            toasterApi.error({
              title: labels.errorTitle,
              description: tProxyMain('selectApplicationToGetStarted'),
            });

            return;
          }

          savedSkill = await aiCreateSkill.mutateAsync({
            applicationId: appTargetId,
            slug: draft.slug.trim(),
            frontmatter: {
              name: draft.name.trim(),
              description: draft.description.trim(),
            },
            body: draft.body,
            publishImmediately,
          });
        } else if (skillId) {
          savedSkill = await updateSkillDraft.mutateAsync({
            skillId,
            body: {
              frontmatter: {
                name: draft.name.trim(),
                description: draft.description.trim(),
              },
              body: draft.body,
            },
          });
          if (publishImmediately) {
            savedSkill = await publishSkill.mutateAsync({ skillId });
          }
        }

        toasterApi.success({
          title: labels.successTitle,
          description: publishImmediately
            ? labels.publishedToast
            : labels.saved,
        });
        if (savedSkill) {
          handleOpenSavedSkill(savedSkill);
        } else {
          handleReturnToSkillsPage();
        }
      } catch (error) {
        if (
          error instanceof SkillsApiError &&
          isAiCreateRejection(error.body)
        ) {
          handleSkillRejection(error.body);

          return;
        }

        toasterApi.error({
          title: labels.errorTitle,
          description: labels.error,
        });
      } finally {
        setPendingAction(null);
      }
    },
    [
      aiCreateSkill,
      appTargetId,
      draft,
      handleOpenSavedSkill,
      handleReturnToSkillsPage,
      handleSkillRejection,
      labels,
      mode,
      publishSkill,
      skillId,
      tProxyMain,
      updateSkillDraft,
    ],
  );
  const handleSaveDraft = useCallback(() => {
    void handleSave({ publishImmediately: false });
  }, [handleSave]);
  const handlePublish = useCallback(() => {
    void handleSave({ publishImmediately: true });
  }, [handleSave]);
  const title = mode === 'create' ? labels.createTitle : labels.editTitle;
  const subtitle =
    mode === 'create' ? labels.createSubtitle : labels.editSubtitle;
  const isPending =
    aiCreateSkill.isPending ||
    updateSkillDraft.isPending ||
    publishSkill.isPending;
  const isSaving = pendingAction === 'save' && isPending;
  const isPublishing = pendingAction === 'publish' && isPending;

  if (mode === 'edit' && isLoading && draft === EMPTY_SKILL_DRAFT) {
    return (
      <HStack h="full" alignItems="center" justifyContent="center">
        <Spinner />
        <Text>{tCommon('loading')}</Text>
      </HStack>
    );
  }

  return (
    <VStack h="full" w="full" alignItems="stretch" gap={0} bg="bg.default">
      <Box
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderBottomColor="border.default"
        bg="bg.subtle"
      >
        <HStack justifyContent="space-between" alignItems="center" gap={3}>
          <VStack alignItems="flex-start" gap={0.5} minW="0">
            <Text fontSize="sm" fontWeight="semibold" color="fg.default">
              {title}
            </Text>
            <Text fontSize="xs" color="fg.muted" lineClamp={1}>
              {subtitle}
            </Text>
          </VStack>
          <HStack gap={1.5}>
            <Button
              variant="ghost"
              size="xs"
              h="7"
              disabled={isPending}
              onClick={handleReturnToSkillsPage}
              title={listLabels.title}
            >
              <ArrowLeft02Icon size={12} />
              {listLabels.title}
            </Button>
            <Button
              variant="outline"
              size="xs"
              h="7"
              loading={isSaving}
              disabled={isPending || validation.hasBlockingErrors}
              onClick={handleSaveDraft}
            >
              {labels.saveDraft}
            </Button>
            <Button
              size="xs"
              h="7"
              loading={isPublishing}
              disabled={isPending || validation.hasBlockingErrors}
              onClick={handlePublish}
              title={labels.safetyReviewHelp}
            >
              {labels.publish}
            </Button>
          </HStack>
        </HStack>
      </Box>
      <Scroll h="full" w="full">
        <Box p={4}>
          <VStack gap={4} alignItems="stretch" w="full">
            {creatorError ? (
              <CreatorErrorPanel
                title={labels.aiCreateRejectedTitle}
                error={creatorError}
              />
            ) : null}
            <SkillEditorForm
              mode={mode}
              draft={draft}
              labels={labels}
              touched={touched}
              updateEffect={draftUpdateEffect}
              onRefineField={handleRefineField}
            />
          </VStack>
        </Box>
      </Scroll>
    </VStack>
  );
}

function CreatorErrorPanel({
  title,
  error,
}: {
  title: string;
  error: AiCreateSkillRejectedResponse;
}) {
  const details = error.details ?? [];
  const issues = error.issues ?? [];

  return (
    <VStack
      alignItems="stretch"
      gap={1.5}
      borderRadius="sm"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.error"
      bg="bg.subtle"
      p={2.5}
    >
      <HStack alignItems="flex-start" gap={1.5}>
        <Box color="fg.error" borderRadius="full" p={0.5} mt={0.5}>
          <CancelCircleIcon size={12} />
        </Box>
        <VStack alignItems="flex-start" gap={0.5} flex="1" minW="0">
          <Text fontSize="xs" fontWeight="semibold" color="fg.error">
            {title}
          </Text>
          <Text fontSize="xs" color="fg.muted" lineHeight="1.6">
            {error.message}
          </Text>
          <Text fontSize="2xs" color="fg.error" lineHeight="1.6">
            {error.reason}
          </Text>
        </VStack>
      </HStack>
      {details.length > 0 && issues.length === 0 ? (
        <VStack alignItems="stretch" gap={1} pl={6}>
          {details.map((detail) => (
            <HStack key={detail} alignItems="flex-start" gap={2}>
              <Box
                mt={1.5}
                w="3px"
                h="3px"
                borderRadius="full"
                bg="fg.error"
                flexShrink={0}
              />
              <Text fontSize="xs" color="fg.default" lineHeight="1.6">
                {detail}
              </Text>
            </HStack>
          ))}
        </VStack>
      ) : null}
      {issues.length > 0 ? (
        <VStack alignItems="stretch" gap={1} pl={6}>
          {issues.map((issue) => (
            <VStack
              key={`${issue.category}:${issue.field}:${issue.message}`}
              alignItems="stretch"
              gap={0.5}
              borderRadius="sm"
              borderWidth="1px"
              borderStyle="solid"
              borderColor="border.error"
              bg="bg.default"
              p={2}
            >
              <HStack gap={1.5} alignItems="center" flexWrap="wrap">
                <Text fontSize="2xs" fontWeight="semibold" color="fg.error">
                  {issue.field}
                </Text>
                <Text fontSize="2xs" color="fg.muted">
                  {issue.category} · {issue.severity}
                </Text>
              </HStack>
              <Text fontSize="xs" color="fg.default" lineHeight="1.6">
                {issue.message}
              </Text>
              {issue.excerpt ? (
                <Text fontSize="2xs" color="fg.muted" lineHeight="1.6">
                  {issue.excerpt}
                </Text>
              ) : null}
            </VStack>
          ))}
        </VStack>
      ) : null}
    </VStack>
  );
}
